import React, { useCallback, useEffect, useRef, useState } from "react";
import styles from '../Dashboard/index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { addNewCourseCategory, editCourseCategory, getAllCourseCategories } from "../../../redux/features/courseCategories/courseCategoriesService";
import { showToast } from "../../../components/ToastMsg";
import BeatLoader from "react-spinners/BeatLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-regular-svg-icons";
import Modal from "../../../components/Modal";
import Pagination from "../../../components/Pagination";
import NoResultsFound from "../../../components/NoResultsFound";
import TableSkeleton from "../../../components/Skeleton";
import { editUser } from "../../../redux/features/users/userService";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import SearchBarOnChange from "../../../components/SearchBarOnChange";

const CourseCategory = () => {

    const dispatch = useDispatch();
    const roleDropdownRef = useRef(null);

    const { isCourseCategoriesLoading, pageCount, courseCategories } = useSelector((state) => state.courseCategory.allCourseCategories);
    const { isCourseCategoryCreating } = useSelector((state) => state.courseCategory.newCourseCategory);
    const { isEditingCourseCategory } = useSelector((state) => state.courseCategory.editedCourseCategory);

    const [formData, setFormData] = useState({
        name: '',
    });

    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setBtnDisable] = useState(false);
    const [skip, setSkip] = useState(0);

    const limit = 10;  // Number of items per page
    const [isModalOpen, setModalOpen] = useState(false);
    const [selectedCourseCategory, setSelectedCourseCategory] = useState(null);
    const [initialCourseCategory, setInitialCourseCategory] = useState(null);
    const [editErrorMsgs, setEditErrorMsgs] = useState({
        name: '',
    });

    const [disabledEditBtn, setEditBtnDisable] = useState(false);
    const [errorMsg, setErrorMsg] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const fetchAllCourseCategories = useCallback(() => {
        dispatch(getAllCourseCategories({
            skip: skip,
            limit,
            search: searchQuery,
        }));
    }, [dispatch, skip, searchQuery]);

    useEffect(() => {
        fetchAllCourseCategories();
    }, [fetchAllCourseCategories]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    // Validate input
    const isValidCourseCategory = () => {
        const errors = {};
        const trimmedName = formData.name.trim();
        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewCourseCategory = async () => {
        if (isValidCourseCategory()) {
            setBtnDisable(true);
            try {
                const response = await dispatch(addNewCourseCategory(formData)).unwrap();
                setBtnDisable(false);
                setFormData({
                    name: '',
                });
                if (roleDropdownRef.current) {
                    roleDropdownRef.current.clearSelection();
                }
                fetchAllCourseCategories();
                showToast('success', response.message);
            }
            catch (error) {
                setBtnDisable(false);
                showToast('error', error.message);
            };
        }
    };

    // Edit courseCategory
    const handleSelectedCourseCategoryChange = (e) => {
        const { name, value } = e.target;
        setErrorMsg(null);
        setSelectedCourseCategory((prev) => ({
            ...prev,
            [name]: value,
        }));

        setEditErrorMsgs((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const isValidEditCourseCategory = () => {
        const errors = {};
        const trimmedName = selectedCourseCategory.name.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        }
        if (Object.keys(errors).length > 0) {
            setEditErrorMsgs(errors);
            return false;
        } else {
            return true;
        }
    };

    function getChangedValues(selectedData) {
        const changedValues = {};
        for (const key in selectedData) {
            if (
                key !== 'id' &&
                selectedData.hasOwnProperty(key) &&
                selectedData[key] !== initialCourseCategory[key]
            ) {
                changedValues[key] = selectedData[key];
            }

            if (key === 'id') {
                changedValues[key] = selectedData[key];
            }
        }
        return changedValues;
    }

    const editCourseCategoryDetails = async () => {
        if (isValidEditCourseCategory(selectedCourseCategory)) {
            const data = getChangedValues(selectedCourseCategory);
            const hasChanges = Object.keys(data).some(key => key !== 'id');
            if (!hasChanges) {
                setErrorMsg('There are no changes to update.');
            } else {
                setEditBtnDisable(true);
                try {
                    const response = await dispatch(editCourseCategory(data)).unwrap();
                    setEditBtnDisable(false);
                    setSelectedCourseCategory(null);
                    setInitialCourseCategory(null);
                    setModalOpen(false);
                    showToast('success', response.message);
                    fetchAllCourseCategories();
                }
                catch (error) {
                    setEditBtnDisable(false);
                    showToast('error', error.message);
                };
            }
        }
    };

    //Activate CourseCategory
    const [isActivateModalOpen, setActivateModalOpen] = useState(false);
    const [isDeactivationModalOpen, setDeactivationModalOpen] = useState(false);
    const [selectedCourseCategoryId, setSelectedCourseCategoryId] = useState(null);

    const closeModal = () => setActivateModalOpen(false);
    const closDeactivationModal = () => setDeactivationModalOpen(false);

    const handleYes = async () => {
        setActivateModalOpen(false);
        try {
            const response = await dispatch(editCourseCategory({ id: selectedCourseCategoryId, status: true })).unwrap();
            setSelectedCourseCategoryId(null);
            setInitialCourseCategory(null);
            showToast('success', response.message);
            fetchAllCourseCategories();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }
    const handleRejectYes = async () => {
        setDeactivationModalOpen(false);
        try {
            const response = await dispatch(editCourseCategory({ id: selectedCourseCategoryId, status: false })).unwrap();
            setSelectedCourseCategoryId(null);
            setInitialCourseCategory(null);
            showToast('success', response.message);
            fetchAllCourseCategories();
        }
        catch (error) {
            showToast('error', error.message);
        };
    }

    const handlePageChange = (pageNumber) => {
        setSkip(pageNumber - 1);
    };

    const handleSearch = (query) => {
        setSearchQuery(query)
        setSkip(0);
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Course Category</h1>
                <div className={styles.formContainer}>
                    <form
                        className={styles.coordinatorForm}
                        noValidate
                    >
                        <div className={`form-input-row`}>
                            <div className={`form-input-col md-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='name' className={`form-label`}>
                                        Name<span className={`required-star`}>*</span>
                                    </label>
                                    <input
                                        type='text'
                                        id='name'
                                        name='name'
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        placeholder='Enter Name'
                                        className={`form-input-default`}
                                    />
                                    {errorMessages.name && (
                                        <p className={`text-danger`}>{errorMessages.name}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className={`btn-primary`}
                            type='button'
                            disabled={disabledBtn}
                            onClick={insertNewCourseCategory}
                        >
                            {isCourseCategoryCreating ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
            <div className={`search-card`}>
                <div className={`search-card-header`}>
                    <h1 className={`form-title`}>Course Categories</h1>
                    <SearchBarOnChange
                        value={searchQuery}
                        onSearch={handleSearch}
                    />
                </div>
                <div className={`search-card-body`}>
                    {isCourseCategoriesLoading ?
                        <TableSkeleton columns={["Name", "Status", "Actions"]} rowCount={10} height={20} />
                        : courseCategories.length > 0 ?
                            <div className={`table-container`}>
                                <table className={`table`}>
                                    <thead className={`thead`}>
                                        <tr>
                                            <th className={`theadColumnOne`}>Name</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className={`tbody`}>
                                        {courseCategories?.map((courseCategory) => (
                                            <tr key={courseCategory.id}>
                                                <td className={`tdataColumnOne`}>
                                                    {courseCategory.name}
                                                </td>
                                                <td>{courseCategory.status ? 'Active' : 'Inactive'}</td>
                                                <td className={`actionsBtn`}>
                                                    <button
                                                        className={courseCategory.status ? `btn-inactive` : `btn-active`}
                                                        onClick={() => {
                                                            setSelectedCourseCategoryId(courseCategory.id);
                                                            courseCategory.status ? setDeactivationModalOpen(true) : setActivateModalOpen(true);
                                                        }
                                                        }>{courseCategory.status ? 'Deactivate' : 'Acitvate'}</button>
                                                    <button
                                                        className={`btn-active`}
                                                        onClick={() => {
                                                            setSelectedCourseCategory(courseCategory);
                                                            setInitialCourseCategory(courseCategory);
                                                            setModalOpen(true);
                                                            setEditErrorMsgs({
                                                                name: ''
                                                            })
                                                        }
                                                        }>Edit</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            :
                            <NoResultsFound />
                    }
                </div>
                <div className={`search-card-footer`}>
                    {pageCount > 0 && (
                        <div className={styles.paginationContainer}>
                            <Pagination
                                currentPage={skip + 1}
                                totalPages={pageCount}
                                onPageChange={handlePageChange}
                            />
                        </div>
                    )}
                </div>
            </div>
            {isModalOpen &&
                <div className={`modal-overlay`}>
                    <div className={`modal-card`}>
                        <div className={`modal-header`}>
                            <h4 className={`modal-title`}>Edit Course Type</h4>
                            <FontAwesomeIcon icon={faXmark} onClick={() => setModalOpen(false)} size="lg" cursor="pointer" />
                        </div>
                        <div className={`modal-body`}>
                            <div className={styles.formContainer}>
                                <form
                                    className={styles.coordinatorForm}
                                    noValidate
                                >
                                    <div className={`form-input-row md-col`}>
                                        <div className={`form-input-group`}>
                                            <label htmlFor='full_name' className={`form-label`}>
                                                Name<span className={`required-star`}>*</span>
                                            </label>
                                            <input
                                                type='text'
                                                id='name'
                                                name='name'
                                                value={selectedCourseCategory.name || ''}
                                                onChange={handleSelectedCourseCategoryChange}
                                                placeholder='Enter Name'
                                                className={`form-input-default`}
                                            />
                                            {editErrorMsgs.name && (
                                                <p className={`text-danger`}>{editErrorMsgs.name}</p>
                                            )}
                                        </div>
                                    </div>
                                    <button
                                        className={`btn-primary`}
                                        type='button'
                                        disabled={disabledEditBtn}
                                        onClick={editCourseCategoryDetails}
                                    >
                                        {isEditingCourseCategory ? <BeatLoader color='#ffffff' /> : `Save`}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {isActivateModalOpen && (
                <Modal
                    title='Course Category Activation'
                    onClose={closeModal}
                    onYes={handleYes}
                    isInput={false}
                    iconName={faCircleCheck}
                    bodyText={`Are you sure you want to activate the Course Category?`}
                    colorType={`var(--green)`}
                />
            )}
            {isDeactivationModalOpen && (
                <Modal
                    title='Course Category Deactivation'
                    onClose={closDeactivationModal}
                    onYes={handleRejectYes}
                    isInput={false}
                    iconName={faCircleXmark}
                    bodyText={`Are you sure you want to deactivate the Course Category?`}
                    colorType={`var(--danger)`}
                />
            )}
        </div>
    );
};

export default CourseCategory;